import { MultiLineChartLine } from '@design/charts/multi-line-chart/MultiLineChart.types';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Toggle } from '@design/components/toggle/Toggle';
import { generatePath } from '@helpers';
import { getHorizonToIndexMapCopy } from '@terminal/components/price-movement/components/price-movement-chart/utils/price-movement-chart-utils';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { AnalyticsLineChartArea } from '@terminal/modules/analytics-line-chart/components/analytics-line-chart-area/AnalyticsLineChartArea';
import {
  useSpxBenchmark,
  useThemePerformance,
} from '@terminal/react-query-hooks/themes/useThemeOverview';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { HorizonSelector } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/horizon-selector/HorizonSelector';
import { Horizon } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MSThemePerformance.module.scss';

const SPX_TAG = 'spx';

const nonIntradayDateFormatter = (index: string) => new Date(index);

const formatData = (
  tsData: { value: number; index: string }[] | undefined,
  dateFormatter: (index: string) => Date
) =>
  tsData?.map(d => ({
    value: d.value,
    date: dateFormatter(d.index),
  })) ?? [];

const chartHorizons: Horizon[] = ['1D', '1W', '1M', '3M', '6M', '1Y'];

export const MSThemePerformance = ({
  msThemeId,
  entityTags,
  themesIds,
}: {
  msThemeId: string;
  entityTags: string[];
  themesIds: string[];
}) => {
  const { t } = useTranslation(['widget']);

  const [showBenchmark, setShowBenchmark] = useState(false);
  const [selectedHorizon, setSelectedHorizon] = useState<Horizon>('1Y');

  const horizonToIndexMap = getHorizonToIndexMapCopy(1);

  const numDays = getHorizonToIndexMapCopy(1).get(selectedHorizon);
  const { data: chartData, isLoading: isThemePerformanceLoading } =
    useThemePerformance(entityTags, numDays);

  const { data: spxData, isFetching: isSpxFetching } = useSpxBenchmark(
    chartData?.aggregate[0]?.index
  );

  const linkToPerformanceTab = generatePath({
    basePath: `${appPaths.themeOverviewMorganStanley}/${msThemeId}`,
    params: {
      [queryKeys.activeSubTab]: 'performance',
    },
  });

  const handleHorizonChange = (horizon: Horizon) => {
    if (horizon === '1D') {
      setShowBenchmark(false);
    }
    setSelectedHorizon(horizon);
  };

  if (isThemePerformanceLoading || isSpxFetching) {
    return (
      <SkeletonLoader
        data-testid="loading-skeleton"
        areas={[
          {
            styles: {
              width: '100%',
              height: '340px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!chartData) {
    return null;
  }

  const multiLineConfig: Record<string, MultiLineChartLine[]> = {
    aggregate: [
      {
        label: t('widget:themeOverview.themePerformance'),
        color: 'var(--icon-default)',
        data: formatData(chartData.aggregate, nonIntradayDateFormatter),
      },
    ],
    benchmark: [
      {
        label: t('widget:themeOverview.benchmark', {
          name: 'SPX',
        }),
        color: 'var(--icon-soft)',
        data: formatData(spxData?.aggregate, nonIntradayDateFormatter),
        isBenchmark: true,
      },
    ],
  };
  return (
    <div data-testid="theme-performance">
      <div className={styles.headerWrapper}>
        <div>
          <div className={styles.titleWrapper}>
            <SectionTitle
              title={t('widget:themeOverview.themePerformance')}
              linkTo={linkToPerformanceTab}
            />
          </div>
          <div className={styles.subTitleWrapper}>
            <div className={styles.description}>
              {showBenchmark && (
                <div className={styles.topCompaniesLineLegend} />
              )}
              {t('widget:themeOverview.topTenCompaniesByRank')}
            </div>
            <Toggle
              isChecked={showBenchmark}
              disabled={selectedHorizon === '1D'}
              onToggle={checked => setShowBenchmark(checked)}
              leftLabel={
                <div className={styles.benchmarkLabelWrapper}>
                  {showBenchmark && (
                    <div className={styles.benchmarkLineLegend} />
                  )}
                  {t('widget:themeOverview.benchmark', {
                    name: 'SPX',
                  })}
                </div>
              }
              data-testid={'breakdown-toggle'}
            />
          </div>
        </div>
        {/* <OneDayReturnPill themeId={theme.id} /> */}
      </div>
      <div className={styles.chartAndHorizonsWrapper}>
        <AnalyticsLineChartArea
          multiLineConfig={multiLineConfig}
          showBenchmark={showBenchmark}
          tooltipSort="desc"
          themes={themesIds}
          selectedHorizon={selectedHorizon}
        />
        <HorizonSelector
          selectedHorizon={selectedHorizon}
          horizons={chartHorizons}
          handleHorizonChange={handleHorizonChange}
          horizonToIndexMap={horizonToIndexMap}
          benchmarkTags={showBenchmark ? [SPX_TAG] : undefined}
          themeId={'fdsfsd'}
        />
      </div>
    </div>
  );
};
